/** Fetch wrapper: bearer token, JSON, and the API error model ``{code, message, details}``. */
import { idToken } from "../auth/AuthContext";

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details: unknown = null,
  ) {
    super(message);
  }
}

export const API = "/api/v1";

export async function request<T>(method: string, path: string, body?: unknown, retry = true): Promise<T> {
  const token = await idToken();
  const response = await fetch(`${API}${path}`, {
    method,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (response.status === 401 && retry && token) {
    await idToken(true);
    return request<T>(method, path, body, false);
  }
  if (!response.ok) {
    let payload: { code?: string; message?: string; details?: unknown } = {};
    try {
      payload = await response.json();
    } catch {
      /* not JSON */
    }
    throw new ApiError(
      response.status,
      payload.code ?? "http_error",
      payload.message ?? `Request failed (${response.status}).`,
      payload.details ?? null,
    );
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body ?? {}),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, body ?? {}),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, body ?? {}),
  delete: <T>(path: string) => request<T>("DELETE", path),
};

/** Ask for a short-lived signed URL, then let the browser download it. */
export async function download(path: string): Promise<void> {
  const separator = path.includes("?") ? "&" : "?";
  const { url } = await api.get<{ url: string }>(`${path}${separator}format=json`);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

export function errorMessage(reason: unknown): string {
  if (reason instanceof ApiError) return reason.message;
  if (reason instanceof Error) return reason.message;
  return "Something went wrong.";
}
